import React, { useState, useEffect } from 'react';
import ProjectsDashboardTable from 'components/dashboard/ProjectsDashboardTable';
import { postRequest, changeDateFormat } from 'utils/_function';
import { makeStyles } from '@material-ui/core/styles';
import Header from 'components/dashboard/Header';
import PurchaseAlertPopup from 'components/map/PurchaseAlertPopup';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import { useHokudenMap } from 'hooks/useHokudenMap'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '1.5em',
  },
  containerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1.5em 1.5em 0',
  },
  leftTitle: {
    margin: 0,
  },
}));

/** Project 一覧ページ */
export default function ProjectsDashboard(props) {

  const {
    user,
    userName,
    accountGroupId,
    accountGroupName,
    isShowPurchaseAlertPopup,
    setIsShowPurchaseAlertPopup,
    currentLang
  } = props;

  const classes = useStyles();
  const [projectsTableData, setProjectsTableData] = useState([]);
  const [alertMesseageProjectInfoList, setAlertMesseageProjectInfoList] = useState(false);
  const [addLoadingList, setAddLoadingList] = useState(false);
  const { t } = useTranslation();

  // --- ページネーション用のStateを追加 ---
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // APIのデフォルト値に合わせる
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // 北陸電力専用地図選択機能ロジック
  const { hokuden_map_url, hokurikuMapProject, hokurikuMouseOver, hokurikuMouseRemove, handleChangeProjectId, qmaxDefaultValue } = useHokudenMap(accountGroupId, projectsTableData);

  /**
   * Project一覧を取得 (ページネーション対応)
   * @param {object|null} exclusiveStartKey - DynamoDBのページネーションキー
   */
  const fetchProjectInfoList = async (exclusiveStartKey = null) => {
    // ローディング中の重複実行を防止
    if (addLoadingList || loadingMore) return;

    if (exclusiveStartKey) {
      setLoadingMore(true);
    } else {
      setAddLoadingList(true);
    }
    setAlertMesseageProjectInfoList(false);
    const errorMessage = '一覧が取得できません。';
    
    const params = {
      accountGroupId: accountGroupId,
      parPage: rowsPerPage,
      ...(exclusiveStartKey && { exclusiveStartKey: exclusiveStartKey }),
    };

    const response = await postRequest(
      'IM_Lambda',
      '/im-getprojectinfolist',
      params
    );
    
    setAddLoadingList(false);
    setLoadingMore(false);

    if (response.errorMessage || !response.statusCode) {
      setAlertMesseageProjectInfoList(errorMessage);
      return;
    }

    switch (response.statusCode) {
      case 'N0000': // 正常終了
        const newData = await createTableData(response.projectInfoList);
        setProjectsTableData(prevData => exclusiveStartKey ? [...prevData, ...newData] : newData);
        setLastEvaluatedKey(response.lastEvaluatedKey || null);
        setAlertMesseageProjectInfoList(false);
        break;
      case 'E0140':
      case 'E0180':
      case 'E0250':
      case 'E0260':
      case 'E0270':
      case 'E0280':
        setAlertMesseageProjectInfoList(errorMessage);
        break;
      default:
        setAlertMesseageProjectInfoList(errorMessage);
        break;
    }
  };

  /** IM_GetProjectInfoList から取得したデータをダッシュボード表示用に整形 */
  const createTableData = async (projectsInfo) => {
    let projectData = [];
    for (let i = 0; i < projectsInfo.length; i++){
      projectData.push({
        projectId: projectsInfo[i].projectId,
        refProjectId: projectsInfo[i].refProjectId,
        latitudeCenter: projectsInfo[i].pjLatCenter,
        longitudeCenter: projectsInfo[i].pjLngCenter,
        category: projectsInfo[i].projectCategory,
        createCsv: projectsInfo[i].CreateCsv,
        henniMax: projectsInfo[i].valAnalysisMax,
        henniAve: projectsInfo[i].valAnalysisAve,
        henniCountOverThreshold: projectsInfo[i].valAnalysisCount,
        henniPercentageOverThreshold: projectsInfo[i].valAnalysisCountRate,
        velocityMax: projectsInfo[i].valVelocityMax,
        velocityAve: projectsInfo[i].valVelocityAve,
        velocityCountOverThreshold: projectsInfo[i].valVelocityCount,
        velocityPercentageOverThreshold: projectsInfo[i].valVelocityCountRate,
        name: projectsInfo[i].projectName,
        createDateTime: changeDateFormat(projectsInfo[i].createDateTime),
        updatedAt: changeDateFormat(projectsInfo[i].updateDateTime),
        aoiCount: projectsInfo[i].countAoi,
        target: projectsInfo[i].Target,
        status: projectsInfo[i].Status,
        remarks: projectsInfo[i].Remarks,
        dates: projectsInfo[i].Dates,
        purchaseType: projectsInfo[i].PurchaseType
      })
    }
    return projectData.map(function(v) {return v;});
  };

  /** accountGroupIdまたはrowsPerPageが変更された時にデータを再取得 */
  useEffect(() => {
    if (!accountGroupId) return;
    setPage(0); // ページをリセット
    setProjectsTableData([]); // 既存のデータをクリア
    fetchProjectInfoList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountGroupId, rowsPerPage]);

  // --- ページネーション用のハンドラ ---
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // 次のページのデータがローカルになければAPIで取得
    const requiredDataCount = (newPage + 1) * rowsPerPage;
    if (lastEvaluatedKey && requiredDataCount > projectsTableData.length) {
      fetchProjectInfoList(lastEvaluatedKey);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    // Stateを更新するとuseEffectが発火してデータが再取得される
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  return (
    <>
      <Header
        t={t}
        user={user}
        userName={userName}
        accountGroupName={accountGroupName}
      ></Header>
      <div className="container">
        <div className={classes.containerTop}>
          <h2 className={classes.leftTitle}>{t("dashboard.title")}</h2>
        </div>
        {/* 北陸電力向け機能 */}
        {Array.isArray(hokurikuMapProject) && hokurikuMapProject.length > 0 &&
          <div style={{padding: '10px 0 0 25px', width: '100%', boxSizing: "border-box"}}>
            <div style={{display: "flex", alignItems: "flex-start"}}>
              <div style={{width: "40%", position: "relative", maxWidth: "800px", minWidth: "800px"}}>
                <img
                  style={{width: '100%', display: "block"}}
                  src={hokuden_map_url}
                  alt=""
                />
                {hokurikuMapProject.map((item, index) => (
                  <div
                    key={item.projectId ?? index}
                    style={{
                      position: "absolute",
                      fontSize: "40px",
                      ...(item.refProjectId === "-xPATkZRSVq5oxo1vG6r-A" && {
                        top: "18%",
                        right: "18%",
                      }),
                      ...(item.refProjectId === "rk0na6baRsOREcvH3Ugexw" && {
                        top: "45%",
                        right: "23%",
                      }),
                      ...(item.refProjectId === "bN33Q-ftS8CkCpapjDceHA" && {
                        top: "40%",
                        left: "49%",
                      }),
                      ...(item.refProjectId === "-3M-t-R6S8S9YPyoAByyyw" && {
                        top: "65%",
                        left: "45%",
                      }),
                      ...(item.refProjectId === "PJ002443" && {
                        top: "35%",
                        left: "25%",
                      }),
                      ...(item.refProjectId === "EM5L3GFMSNibxiFBBY-IKw" && {
                        top: "62%",
                        left: "22%",
                      }),
                    }}
                  >
                    <Link
                      to={{ pathname: `/map` }}
                      style={{color: "#0000ee"}}
                      onClick={() =>
                        handleChangeProjectId(
                          item.refProjectId,
                          currentLang,
                          item.latitudeCenter,
                          item.longitudeCenter,
                          JSON.parse(item.dates.replace(/'/g, '"')),
                          item.projectId,
                          qmaxDefaultValue,
                          item.name
                        )
                      }
                      onMouseEnter={() => hokurikuMouseOver(item.refProjectId)}
                      onMouseLeave={() => hokurikuMouseRemove()}
                    >
                      {item.refProjectId === "-xPATkZRSVq5oxo1vG6r-A" && <>①</>}
                      {item.refProjectId === "rk0na6baRsOREcvH3Ugexw" && <>②</>}
                      {item.refProjectId === "bN33Q-ftS8CkCpapjDceHA" && <>③</>}
                      {item.refProjectId === "-3M-t-R6S8S9YPyoAByyyw" && <>④</>}
                      {item.refProjectId === "PJ002443" && <>⑤</>}
                      {item.refProjectId === "EM5L3GFMSNibxiFBBY-IKw" && <>⑥</>}
                    </Link>
                  </div>
                ))}
              </div>
              <div style={{width: "50%", textAlign: "left", marginLeft: "10px"}}>
                <div style={{marginBottom: "10px"}}>
                  <div><span style={{fontSize: "21px", paddingRight: "5px"}}>①</span>新_北側</div>
                  <div><span style={{fontSize: "21px", paddingRight: "5px"}}>②</span>新＆常＆神_南側(新)_東側(常＆神)</div>
                  <div><span style={{fontSize: "21px", paddingRight: "5px"}}>③</span>常＆神＆手_西側(常＆神)_北東側(手)</div>
                  <div><span style={{fontSize: "21px", paddingRight: "5px"}}>④</span>手&大_東側</div>
                  <div><span style={{fontSize: "21px", paddingRight: "5px"}}>⑤</span>手_北西側</div>
                  <div><span style={{fontSize: "21px", paddingRight: "5px"}}>⑥</span>手&大_西側</div>
                </div>
                <div>
                  <div>・「新」：新川水力センター</div>
                  <div>・「常」：常願寺水力センター</div>
                  <div>・「神」：神通水力センター</div>
                  <div>・「手」：手取水力センター</div>
                  <div>・「大」：大野水力センター</div>
                </div>
              </div>
            </div>
          </div>
        }
        <div className={classes.container}>
          <ProjectsDashboardTable
            t={t}
            currentLang={currentLang}
            projectsTableData={projectsTableData}
            alertMesseageProjectInfoList={alertMesseageProjectInfoList}
            addLoadingList={addLoadingList}
            accountGroupId={accountGroupId}
            setIsShowPurchaseAlertPopup={setIsShowPurchaseAlertPopup}
            // --- ページネーション関連のpropsを渡す ---
            count={lastEvaluatedKey ? projectsTableData.length + 1 : projectsTableData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            loadingMore={loadingMore}
          ></ProjectsDashboardTable>
        </div>
      </div>
      {/* 非購入AOIアラートポップアップ */}
      <PurchaseAlertPopup
        isShowPurchaseAlertPopup={isShowPurchaseAlertPopup}
        setIsShowPurchaseAlertPopup={setIsShowPurchaseAlertPopup}
      />
    </>
  );
}
